import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import StudentSelector from "@/pages/ogretmen/components/StudentSelector";
import {
  useTeacherStudents,
  useTeacherStudentExams,
  useTeacherStudentPerformance,
  useTeacherStudentWeakTopics,
} from "@/hooks/useTeacher";

export default function OgrenciAnalizleri() {
  const { data: students, loading: studentsLoading, error: studentsError } = useTeacherStudents();
  const [activeUserId, setActiveUserId] = useState<string | null>(null);

  const list = students ?? [];
  const activeStudent = list.find((s) => s.userId === activeUserId) ?? list[0] ?? null;

  const exams = useTeacherStudentExams(activeStudent?.userId ?? null);
  const perf = useTeacherStudentPerformance(activeStudent?.userId ?? null);
  const weak = useTeacherStudentWeakTopics(activeStudent?.userId ?? null);

  if (studentsLoading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;

  if (studentsError) {
    return (
      <div className="space-y-6">
        <SectionHeader title="Öğrenci Analizleri" description="Öğrencilerinin çalışma ve gelişim verileri." />
        <EmptyState icon="ri-error-warning-line" title="Veriler yüklenemedi" description={studentsError} />
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="space-y-6">
        <SectionHeader title="Öğrenci Analizleri" description="Öğrencilerinin çalışma ve gelişim verileri." />
        <EmptyState
          icon="ri-line-chart-line"
          title="Henüz öğrenci yok"
          description='"Öğrencilerim" sayfasından öğrenci atadığında analizleri burada görünecek.'
        />
        <PanelLinks
          links={[{ label: "Öğrencilerim", href: "/ogretmen/ogrencilerim", icon: "ri-group-line" }]}
        />
      </div>
    );
  }

  const examList = exams.data ?? [];
  const perfList = perf.data ?? [];
  const weakList = weak.data ?? [];

  const latest = examList[0];
  const best = examList.length
    ? examList.reduce((max, r) => (r.net > max.net ? r : max), examList[0])
    : null;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Öğrenci Analizleri"
        description={`${activeStudent?.fullName ?? "Öğrencinin"} deneme ve ders bazlı gelişimi.`}
      />
      <StudentSelector
        students={list}
        activeUserId={activeStudent?.userId ?? null}
        onSelect={setActiveUserId}
      />

      {/* Deneme özeti */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">Son deneme neti</p>
          <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">{latest ? latest.net : "—"}</p>
          {latest && <p className="mt-1 text-xs text-foreground-500">{latest.name}</p>}
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">En yüksek net</p>
          <p className="mt-2 font-heading font-bold text-2xl text-accent-700">{best ? best.net : "—"}</p>
          {best && <p className="mt-1 text-xs text-foreground-500">{best.name}</p>}
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">Çözülen deneme</p>
          <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">{examList.length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Deneme geçmişi */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="font-heading font-semibold text-base text-foreground-950">Deneme Geçmişi</h2>
          {exams.loading ? (
            <p className="mt-3 text-sm text-foreground-500">Yükleniyor...</p>
          ) : examList.length === 0 ? (
            <p className="mt-3 text-sm text-foreground-500">Henüz deneme sonucu yok.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="py-2 pr-3 text-left font-semibold text-foreground-600">Deneme</th>
                    <th className="py-2 pr-3 text-center font-semibold text-foreground-600">D</th>
                    <th className="py-2 pr-3 text-center font-semibold text-foreground-600">Y</th>
                    <th className="py-2 text-center font-semibold text-foreground-600">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {examList.map((e) => (
                    <tr key={e.id} className="border-b border-background-200">
                      <td className="py-2 pr-3 text-foreground-800">
                        <p className="font-medium">{e.name}</p>
                        <p className="text-xs text-foreground-500">{e.date}</p>
                      </td>
                      <td className="py-2 pr-3 text-center text-accent-700 font-semibold">{e.correct}</td>
                      <td className="py-2 pr-3 text-center text-red-600 font-semibold">{e.wrong}</td>
                      <td className="py-2 text-center text-foreground-950 font-bold">{e.net}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Ders bazlı performans */}
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="font-heading font-semibold text-base text-foreground-950">Ders Bazlı Performans</h2>
          {perf.loading ? (
            <p className="mt-3 text-sm text-foreground-500">Yükleniyor...</p>
          ) : perfList.length === 0 ? (
            <p className="mt-3 text-sm text-foreground-500">Henüz performans verisi yok.</p>
          ) : (
            <div className="mt-3 space-y-3">
              {perfList.map((p) => {
                const pct = p.maxNet > 0 ? Math.min(Math.round((p.net / p.maxNet) * 100), 100) : 0;
                return (
                  <div key={p.subject}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-foreground-800">{p.subject}</span>
                      <span className="text-xs text-foreground-500">
                        {p.net} / {p.maxNet} net
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-background-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Eksik konular */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading font-semibold text-base text-foreground-950">Eksik Konular</h2>
        {weak.loading ? (
          <p className="mt-3 text-sm text-foreground-500">Yükleniyor...</p>
        ) : weakList.length === 0 ? (
          <p className="mt-3 text-sm text-foreground-500">Henüz eksik konu tespit edilmedi.</p>
        ) : (
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {weakList.map((w) => (
              <li
                key={w.id}
                className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100/50 px-3 py-2.5"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0">
                  <i className="ri-alert-line" aria-hidden="true"></i>
                </span>
                <span className="min-w-0 flex-1 text-sm text-foreground-800 truncate">
                  {w.subject} — {w.topic}
                </span>
                <span className="text-sm font-semibold text-foreground-700 whitespace-nowrap">%{w.mastery}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <PanelLinks
        links={[
          { label: "Öğrencilerim", href: "/ogretmen/ogrencilerim", icon: "ri-group-line" },
          { label: "AI Öğrenci Geçmişi", href: "/ogretmen/ai-gecmisi", icon: "ri-brain-line" },
        ]}
      />
    </div>
  );
}